import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  Monitor,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for best coaching in Ghazipur. Please share course details. Thank you.",
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "340+", label: "Students Enrolled", icon: Users },
  { value: "95%", label: "Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
  { value: "6+", label: "Courses Offered", icon: BookOpen },
];

const COURSES = [
  {
    label: "Class 8 Foundation",
    badge: "8th",
    color: "bg-blue-50 border-blue-200 text-blue-700",
    to: "/courses/class-8-foundation",
    desc: "Build strong basics across all subjects",
  },
  {
    label: "Class 9 Foundation",
    badge: "9th",
    color: "bg-emerald-50 border-emerald-200 text-emerald-700",
    to: "/courses/class-9-foundation",
    desc: "Advance concepts, prepare for boards",
  },
  {
    label: "Class 10 Board Prep",
    badge: "10th",
    color: "bg-amber-50 border-amber-200 text-amber-700",
    to: "/courses/class-10-board-preparation",
    desc: "Ace CBSE boards with expert coaching",
  },
  {
    label: "Class 11 Foundation",
    badge: "11th",
    color: "bg-violet-50 border-violet-200 text-violet-700",
    to: "/courses/class-11-foundation",
    desc: "Science & Commerce streams",
  },
  {
    label: "Class 12 Board Prep",
    badge: "12th",
    color: "bg-rose-50 border-rose-200 text-rose-700",
    to: "/courses/class-12-board-preparation",
    desc: "Final board excellence for all streams",
  },
  {
    label: "Computer Science",
    badge: "CS",
    color: "bg-teal-50 border-teal-200 text-teal-700",
    to: "/courses/computer-science",
    desc: "Java, Python, C",
  },
];

const WHY_SSS = [
  {
    icon: Brain,
    title: "Concept-Based Teaching",
    desc: "We focus on building deep understanding, not just rote learning. Every topic is explained with real-life examples and practical applications.",
  },
  {
    icon: Users,
    title: "Small Batch Sizes",
    desc: "Max Small, focused batches ensure every student gets individual attention, doubt clearing and personal mentorship.",
  },
  {
    icon: Zap,
    title: "AI-Powered Smart Classroom",
    desc: "Our tech-enabled classrooms use AI performance tracking to identify each student's strengths and weaknesses in real time.",
  },
  {
    icon: TrendingUp,
    title: "Weekly Tests & Analytics",
    desc: "Regular chapter tests, mock exams and detailed analytics help students track progress and improve consistently.",
  },
  {
    icon: Shield,
    title: "Experienced & Dedicated Faculty",
    desc: "35+ subject-matter experts with years of teaching experience, dedicated to student success and exam readiness.",
  },
  {
    icon: Award,
    title: "95% Success Rate",
    desc: "Our students consistently score high in CBSE boards, school exams and competitive assessments — proven track record since 2025.",
  },
  {
    icon: BookOpen,
    title: "Comprehensive Study Materials",
    desc: "Printed + digital notes, practice worksheets, past-year papers and revision modules — all included in course fees.",
  },
  {
    icon: Target,
    title: "Parent–Teacher Transparency",
    desc: "Monthly progress reports, parent-teacher meetings and real-time updates keep parents involved in their child's growth.",
  },
];

const TESTIMONIALS = [
  {
    name: "Priya Sharma",
    class: "Class 10, 2025",
    rating: 5,
    text: "SSS Achievers is truly the best coaching in Ghazipur. My daughter scored 94% in boards thanks to the focused teaching and weekly tests. The faculty is outstanding!",
  },
  {
    name: "Rahul Gupta",
    class: "Class 12 Science, 2025",
    rating: 5,
    text: "I joined for Class 12 Physics and Chemistry. The concept-based approach helped me clear my doubts completely. Highly recommend SSS Achievers to every student in Ghazipur.",
  },
  {
    name: "Sunita Yadav (Parent)",
    class: "Parent of Class 9 Student",
    rating: 5,
    text: "Monthly reports and regular parent meetings make us feel so connected to our child's progress. The teachers are caring and professional. Best coaching institute in Ghazipur!",
  },
  {
    name: "Amit Singh",
    class: "Class 8 Foundation, 2025",
    rating: 5,
    text: "Small batch size means our teacher knows every student by name. I improved from 65% to 88% in just one semester. SSS Achievers is amazing!",
  },
];

const COMPARISON = [
  {
    feature: "Batch Size",
    sss: "Small Batch (focused)",
    others: "50+ students (crowded)",
  },
  {
    feature: "Faculty Qualification",
    sss: "Subject experts with 5+ years",
    others: "Mixed/varying quality",
  },
  {
    feature: "Performance Tracking",
    sss: "AI-powered weekly analytics",
    others: "Manual, occasional",
  },
  {
    feature: "Study Materials",
    sss: "Included (printed + digital)",
    others: "Extra cost or none",
  },
  {
    feature: "Parent Updates",
    sss: "Monthly reports + meetings",
    others: "Rarely communicated",
  },
  {
    feature: "Demo Class",
    sss: "Free before enrollment",
    others: "Usually not offered",
  },
  {
    feature: "Doubt Clearing",
    sss: "Dedicated sessions",
    others: "Limited or extra fee",
  },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function BestCoachingGhazipur() {
  return (
    <>
      <Helmet>
        <title>
          Best Coaching in Ghazipur | SSS Achievers – #1 Coaching Institute
        </title>
        <meta
          name="description"
          content="SSS Achievers is the best coaching institute in Ghazipur, UP. Expert coaching for Class 8–12, 95% success rate, 340+ students, AI smart classrooms. Free demo class available. Enroll now at Ghazipur's #1 coaching center."
        />
        <meta
          name="keywords"
          content="best coaching in Ghazipur, best coaching institute in Ghazipur, top coaching center Ghazipur, coaching classes Ghazipur UP, best coaching for Class 10 Ghazipur, best coaching for Class 12 Ghazipur, SSS Achievers Ghazipur, coaching in Ghazipur Uttar Pradesh, best tuition in Ghazipur, number 1 coaching Ghazipur, top ranked coaching Ghazipur, CBSE coaching Ghazipur"
        />
        <link
          rel="canonical"
          href="https://www.sssachievers.com/best-coaching-ghazipur"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Best Coaching in Ghazipur | SSS Achievers – #1 Coaching Institute"
        />
        <meta
          property="og:description"
          content="Ghazipur's #1 coaching institute – SSS Achievers. 95% success rate, 340+ students, expert faculty, AI classrooms. Free demo class. Enroll today!"
        />
        <meta
          property="og:url"
          content="https://www.sssachievers.com/best-coaching-ghazipur"
        />
        <meta
          property="og:image"
          content="https://www.sssachievers.com/logo512.png"
        />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Coaching in Ghazipur | SSS Achievers"
        />
        <meta
          name="twitter:description"
          content="Ghazipur's best coaching institute. 95% success rate, expert faculty, AI classrooms. Free demo class at SSS Achievers."
        />
        <meta
          name="twitter:image"
          content="https://www.sssachievers.com/logo512.png"
        />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Best Coaching in Ghazipur", "Top Coaching Institute Ghazipur"],
                "description": "SSS Achievers is the best coaching institute in Ghazipur, Uttar Pradesh offering expert coaching for Class 8–12 with a 95% success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "image": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "email": "info@sssachievers.com",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "openingHoursSpecification": [
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
                    "opens": "09:00",
                    "closes": "19:00"
                  }
                ],
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                },
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Coaching Courses",
                  "itemListElement": [
                    { "@type": "Course", "name": "Class 8 Foundation", "url": "https://www.sssachievers.com/courses/class-8-foundation" },
                    { "@type": "Course", "name": "Class 9 Foundation", "url": "https://www.sssachievers.com/courses/class-9-foundation" },
                    { "@type": "Course", "name": "Class 10 Board Preparation", "url": "https://www.sssachievers.com/courses/class-10-board-preparation" },
                    { "@type": "Course", "name": "Class 11 Foundation", "url": "https://www.sssachievers.com/courses/class-11-foundation" },
                    { "@type": "Course", "name": "Class 12 Board Preparation", "url": "https://www.sssachievers.com/courses/class-12-board-preparation" },
                    { "@type": "Course", "name": "Computer Science", "url": "https://www.sssachievers.com/courses/computer-science" }
                  ]
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best coaching institute in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is widely regarded as the best coaching institute in Ghazipur, UP. With a 95% success rate, 35+ expert educators and AI-powered smart classrooms, SSS Achievers offers unmatched quality education for Class 8–12 students."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is SSS Achievers good for Class 10 board preparation in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers is the top choice for Class 10 CBSE board preparation in Ghazipur. Students benefit from regular mock tests, answer-writing practice, small, focused batches, and dedicated doubt-clearing sessions."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers offer a free demo class?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class before enrollment. You can attend a session and experience the teaching quality, classroom environment and faculty interaction before making a decision."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the fee structure at SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "The fee structure at SSS Achievers is course-specific and competitively priced. Installment options are available. Contact us at +91 99185 19991 or WhatsApp for the latest fee details."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Where is SSS Achievers located in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is located at 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, Uttar Pradesh – 233001. It is easily accessible from all major areas of Ghazipur city."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Best Coaching in Ghazipur", "item": "https://www.sssachievers.com/best-coaching-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ════════════════════════════════════════════════
            HERO — primary keyword in H1
        ════════════════════════════════════════════════ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav
              aria-label="breadcrumb"
              className="mb-6 flex items-center gap-2 text-sm text-slate-500"
            >
              <Link to="/" className="hover:text-[#0097b2] transition">
                Home
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">
                Best Coaching in Ghazipur
              </span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fade}
                className="space-y-6"
              >
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">
                    Ghazipur's #1 Rated Coaching Institute
                  </span>
                </div>

                {/* H1 — primary keyword */}
                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best Coaching in{" "}
                  <span className="text-[#0097b2]">Ghazipur</span> for Class
                  8–12
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers is Ghazipur's most trusted coaching institute —
                  combining expert faculty, AI-powered smart classrooms and
                  proven teaching methods to help students of Class 8–12 achieve
                  their academic best.
                </p>

                {/* CTA buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={WA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition"
                  >
                    Book Free Demo Class
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <a
                    href={`tel:${PHONE}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition"
                  >
                    <Phone className="w-4 h-4" />
                    Call Now: +91 99185 19991
                  </a>
                </div>

                {/* Trust micro-signals */}
                <div className="flex flex-wrap gap-4 pt-2">
                  {[
                    "Free Demo Class",
                    "No Hidden Fees",
                    "CBSE & All Boards",
                    "Small Batches",
                  ].map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 text-sm text-slate-600"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" />
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Stats card panel */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-2 gap-4"
              >
                {STATS.map(({ value, label, icon: Icon }, i) => (
                  <div
                    key={label}
                    className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">
                      {value}
                    </span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">
                      {label}
                    </span>
                  </div>
                ))}

                {/* Rating card */}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]"
                        />
                      ))}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">
                      Based on 340+ student reviews
                    </p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">
                      Est.
                    </p>
                    <p className="text-3xl font-bold">2025</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            WHY SSS IS THE BEST
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-sm font-semibold text-[#0097b2] uppercase tracking-wider mb-2"
              >
                Why Students Choose Us
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                What Makes SSS Achievers the{" "}
                <span className="text-[#0097b2]">Best Coaching Institute</span>{" "}
                in Ghazipur
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-4 text-slate-600 max-w-2xl mx-auto text-base"
              >
                We go beyond textbooks — our student-first approach has
                consistently delivered results that speak for themselves.
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {WHY_SSS.map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  custom={i}
                  variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">
                    {title}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            COURSES OFFERED (Cards)
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                Courses Offered at{" "}
                <span className="text-[#0097b2]">Ghazipur's Best Coaching</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">
                Comprehensive coaching from Class 8 to 12 and Computer Science
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {COURSES.map(({ label, badge, color, to, desc }, i) => (
                <motion.div
                  key={to}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  custom={i}
                  variants={fade}
                >
                  <Link
                    to={to}
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                    className={`group block p-6 rounded-2xl border-2 ${color} bg-white hover:shadow-xl transition-all duration-300`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span
                        className={`text-xs font-bold px-3 py-1.5 rounded-full ${color}`}
                      >
                        {badge}
                      </span>
                      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#0097b2] group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-800 mb-2">
                      {label}
                    </h3>
                    <p className="text-slate-500 text-sm">{desc}</p>
                    <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#0097b2]">
                      View Course Details <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/courses"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md"
              >
                Explore All Courses <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            COMPARISON TABLE
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                SSS Achievers vs. Other Coaching Centers in Ghazipur
              </motion.h2>
              <p className="mt-3 text-slate-500 text-sm max-w-xl mx-auto">
                See why students and parents consistently choose SSS Achievers
                as the best coaching in Ghazipur
              </p>
            </div>

            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              {/* Header */}
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">
                  SSS Achievers ✓
                </div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">
                  Others
                </div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div
                  key={feature}
                  className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <div className="px-5 py-4 font-semibold text-slate-700">
                    {feature}
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">
                    {others}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            TESTIMONIALS
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                What Students Say About{" "}
                <span className="text-[#0097b2]">
                  Best Coaching in Ghazipur
                </span>
              </motion.h2>
              <p className="mt-3 text-slate-500 text-sm">
                Real reviews from real students and parents
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, class: cls, rating, text }, i) => (
                <motion.div
                  key={name}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.15 }}
                  custom={i}
                  variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col"
                >
                  {/* Stars */}
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => (
                      <Star
                        key={s}
                        className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]"
                      />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">
                    "{text}"
                  </p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">
                      {name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            CAMPUS GLIMPSE — real photos
        ════════════════════════════════════════════════ */}
        <section className="py-14 md:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold"
              >
                A Glimpse of <span className="text-[#0097b2]">SSS Achievers, Ghazipur</span>
              </motion.h2>
              <p className="mt-2 text-slate-500 text-sm">Real classrooms. Real results. Real students.</p>
            </div>

            {/* Row 1 — 3 images */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {[
                {
                  src: "/images/SSS%20Achievers%20classroom.avif",
                  alt: "SSS Achievers AI-powered smart classroom in Ghazipur — modern digital learning environment for Class 8-12 students at the best coaching institute",
                  caption: "🏫 AI-Powered Smart Classroom",
                },
                {
                  src: "/images/sss%20achievers%20building%20photo.avif",
                  alt: "SSS Achievers coaching institute building in Ghazipur — Badi Bagh campus, Opp. Raj Nursing Home, Uttar Pradesh 233001",
                  caption: "🏢 Our Campus — Badi Bagh, Ghazipur",
                },
                {
                  src: "/images/sss%20achiever%20new%20flex%20board.avif",
                  alt: "SSS Achievers coaching institute signboard in Ghazipur — ICSE & CBSE Classes 8th to 12th, located at Badi Bagh Ghazipur Uttar Pradesh",
                  caption: "🏫 SSS Achievers — Ghazipur Signboard",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="224"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>

            {/* Row 2 — 2 wider images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  src: "/images/sss%20board%20result%20award%20ceremony.avif",
                  alt: "SSS Achievers board result award ceremony in Ghazipur — CBSE students receiving academic excellence awards at the best coaching institute 2025",
                  caption: "🏆 Board Result Award Ceremony 2025",
                },
                {
                  src: "/images/gallery/parents-meeting.jpg",
                  alt: "SSS Achievers parent-teacher meeting in Ghazipur — monthly progress discussion between faculty and parents at the best coaching institute",
                  caption: "🤝 Parent–Teacher Meeting — SSS Achievers",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="900"
                    height="256"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            INFRASTRUCTURE HIGHLIGHTS
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-3xl md:text-4xl font-bold">
                  World-class Infrastructure at{" "}
                  <span className="text-[#0097b2]">
                    Ghazipur's Best Coaching
                  </span>
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  SSS Achievers invests in the best learning environment so
                  students can focus entirely on their growth. Our facilities
                  are designed to match what top-tier institutes provide — right
                  here in Ghazipur.
                </p>
                <div className="space-y-4">
                  {[
                    {
                      icon: Monitor,
                      title: "AI-Powered Smart Classrooms",
                      desc: "Digital boards, projectors, real-time performance analytics",
                    },
                    {
                      icon: BookOpen,
                      title: "Well-Stocked Study Library",
                      desc: "Reference books, NCERT solutions, past papers for all classes",
                    },
                    {
                      icon: Users,
                      title: "Dedicated Doubt-Clearing Lab",
                      desc: "Scheduled one-on-one and group doubt sessions",
                    },
                    {
                      icon: Shield,
                      title: "Safe & Disciplined Environment",
                      desc: "CCTV monitored, structured schedule, zero-distraction campus",
                    },
                  ].map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#0097b2]" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">
                          {title}
                        </p>
                        <p className="text-slate-500 text-xs mt-0.5">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Location card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg"
              >
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">
                    Find Us in Ghazipur
                  </span>
                </div>

                {/* Embedded map */}
                {/* <iframe
                  title="SSS Achievers Location Ghazipur"
                  src="<iframe src="
                  https:width="100%" //www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>"
                  height="240"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                /> */}

                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="600"
                  height="240"
                 style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                ></iframe>

                <div className="p-5 space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#0097b2] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-600">
                      96/B, Badi Bagh, Opp. Raj Nursing Home,
                      <br />
                      Ghazipur, Uttar Pradesh – 233001
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                    <a
                      href={`tel:${PHONE}`}
                      className="text-sm text-[#0097b2] font-semibold hover:underline"
                    >
                      +91 99185 19991
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                    <p className="text-sm text-slate-600">
                      Mon–Sat: 9:00 AM – 7:00 PM
                    </p>
                  </div>
                  <a
                    href="https://maps.google.com/?q=96/B+Badi+Bagh+Ghazipur+UP+233001"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0097b2] px-4 py-2 rounded-lg hover:bg-[#007a91] transition"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    Get Directions on Google Maps
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            FAQ — targets "People Also Ask" in Google
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                Frequently Asked Questions
              </motion.h2>
              <p className="mt-3 text-slate-500 text-sm">
                Everything you need to know about the best coaching in Ghazipur
              </p>
            </div>
            <FAQAccordion
              faqs={[
                {
                  q: "Which is the best coaching institute in Ghazipur?",
                  a: "SSS Achievers is widely recognized as the best coaching institute in Ghazipur, UP. With a 95% student success rate, 35+ expert educators, AI smart classrooms and small batch sizes, SSS Achievers offers unmatched coaching quality for Class 8–12 students in Ghazipur and surrounding areas.",
                },
                {
                  q: "Is SSS Achievers good for Class 10 board preparation?",
                  a: "Absolutely. SSS Achievers is the top choice for Class 10 CBSE board preparation in Ghazipur. Our curriculum includes comprehensive subject coverage, answer-writing practice, full mock tests, and dedicated doubt-clearing sessions — all designed to maximize board exam scores.",
                },
                {
                  q: "Does SSS Achievers offer a free demo class?",
                  a: "Yes — SSS Achievers offers a completely free demo class before enrollment. You can experience our AI smart classroom, meet the faculty and understand our teaching methodology before making any commitment.",
                },
                {
                  q: "What is the batch size at SSS Achievers Ghazipur?",
                  a: "Each batch at SSS Achievers has a intentionally small. This ensures every student receives individual attention, regular feedback and personalized guidance from faculty.",
                },
                {
                  q: "What is the fee for coaching at SSS Achievers?",
                  a: "Fees at SSS Achievers are course-specific and competitively priced for Ghazipur. Installment payment options are available. Contact us at +91 99185 19991 or WhatsApp for the current fee structure.",
                },
                {
                  q: "Where is SSS Achievers located in Ghazipur?",
                  a: "SSS Achievers is located at 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, Uttar Pradesh – 233001. It is easily accessible from all major areas including Sadar, Zamania, Saidpur and surrounding tehsils.",
                },
                {
                  q: "Which classes does SSS Achievers offer coaching for?",
                  a: "SSS Achievers offers coaching for Class 8, Class 9, Class 10 (Board Preparation), Class 11, Class 12 (Board Preparation) and a dedicated Computer Science course for Class 8–12 students.",
                },
              ]}
            />
          </div>
        </section>

        {/* ════════════════════════════════════════════════
            FINAL CTA
        ════════════════════════════════════════════════ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">
                  Ghazipur's #1 Coaching Institute
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Join the Best Coaching in Ghazipur Today
              </h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Limited seats available. Book your free demo class now and
                experience why 340+ students trust SSS Achievers for their
                academic success.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base"
                >
                  Book Free Demo Class
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href={`tel:${PHONE}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base"
                >
                  <Phone className="w-5 h-5" />
                  +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">
                📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001
              </p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
